import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useMemo } from 'react';
import { V90_CITY_SUGGESTIONS } from '@/constants';
import { getBirthdayAge, isBirthdayAgeValid, MAX_AGE, MIN_AGE } from '@/lib/birthday';

export const MIN_HEIGHT_CM = 50;
export const MAX_HEIGHT_CM = 280;

/** Single source of truth for all profile field validators. */
export class ProfileField {
  static createNameSchema(t: TFunction) {
    return z
      .string()
      .trim()
      .min(1, t('profile:errors.nameRequired', { defaultValue: 'nameRequired' }) as string);
  }

  static createBirthdaySchema(t: TFunction) {
    return z
      .union([
        z.literal(''),
        z.iso.date(
          t('profile:errors.birthdayInvalid', { defaultValue: 'birthdayInvalid' }) as string
        ),
      ])
      .superRefine((value, ctx) => {
        if (value === '' || isBirthdayAgeValid(value)) return;

        const age = getBirthdayAge(value);
        if (age === null) return;

        if (age < MIN_AGE) {
          ctx.addIssue({
            code: 'custom',
            message: t('profile:errors.birthdayUnder18', {
              defaultValue: 'birthdayUnder18',
            }) as string,
          });
          return;
        }

        if (age > MAX_AGE) {
          ctx.addIssue({
            code: 'custom',
            message: t('profile:errors.birthdayTooOld', {
              defaultValue: 'birthdayTooOld',
            }) as string,
          });
        }
      });
  }

  static createBioSchema(t: TFunction) {
    return z
      .string()
      .max(300, t('profile:errors.bioTooLong', { defaultValue: 'bioTooLong' }) as string);
  }

  static createHeightSchema(t: TFunction) {
    return z
      .number()
      .min(
        MIN_HEIGHT_CM,
        t('profile:errors.heightUnrealistic', { defaultValue: 'heightUnrealistic' }) as string
      )
      .max(
        MAX_HEIGHT_CM,
        t('profile:errors.heightUnrealistic', { defaultValue: 'heightUnrealistic' }) as string
      )
      .optional();
  }

  static createPronounsSchema(t: TFunction) {
    return z
      .string()
      .min(1, t('profile:errors.pronounsRequired', { defaultValue: 'pronounsRequired' }) as string);
  }

  static createCitySchema(t: TFunction) {
    return z
      .string()
      .min(1, t('profile:errors.cityRequired', { defaultValue: 'cityRequired' }) as string)
      .refine(
        value => V90_CITY_SUGGESTIONS.some(city => city.toLowerCase() === value.toLowerCase()),
        t('profile:errors.cityNotInList', { defaultValue: 'cityNotInList' }) as string
      );
  }

  static createSelectedGendersSchema(t: TFunction) {
    return z
      .array(z.string())
      .min(1, t('profile:errors.genderRequired', { defaultValue: 'genderRequired' }) as string);
  }

  static createSelectedOrientationsSchema(t: TFunction) {
    return z
      .array(z.string())
      .min(
        1,
        t('profile:errors.orientationRequired', { defaultValue: 'orientationRequired' }) as string
      );
  }

  static createSelectedLanguagesSchema(t: TFunction) {
    return z
      .array(z.string())
      .min(1, t('profile:errors.languageRequired', { defaultValue: 'languageRequired' }) as string);
  }

  static createSelectedEthnicitiesSchema() {
    return z.array(z.string());
  }

  static createSelectedInterestsSchema() {
    return z.array(z.string());
  }

  static createSelectedOpenToSchema() {
    return z.array(z.string());
  }

  static createSelectedRelationshipTypesSchema() {
    return z.array(z.string());
  }

  static createRelationshipStatusSchema(t: TFunction) {
    return z.string().refine(
      value => value.trim().length > 0,
      t('profile:errors.relationshipStatusRequired', {
        defaultValue: 'relationshipStatusRequired',
      }) as string
    );
  }

  static createSelectedKinksSchema() {
    return z.array(z.string());
  }

  static createPhotosSchema(t: TFunction) {
    return z
      .array(
        z.object({
          id: z.string().min(1),
          url: z.string().min(1),
          path: z.string().optional(),
          position: z.number().optional(),
          createdAt: z.string().optional(),
        })
      )
      .min(2, t('profile:errors.photosRequired', { defaultValue: 'photosRequired' }) as string);
  }
}

const createProfileSchema = (t: TFunction) =>
  z.object({
    name: ProfileField.createNameSchema(t),
    birthday: ProfileField.createBirthdaySchema(t),
    bio: ProfileField.createBioSchema(t),
    height: ProfileField.createHeightSchema(t),
    pronouns: ProfileField.createPronounsSchema(t),
    city: ProfileField.createCitySchema(t),
    selectedGenders: ProfileField.createSelectedGendersSchema(t),
    selectedOrientations: ProfileField.createSelectedOrientationsSchema(t),
    selectedLanguages: ProfileField.createSelectedLanguagesSchema(t),
    selectedEthnicities: ProfileField.createSelectedEthnicitiesSchema(),
    selectedInterests: ProfileField.createSelectedInterestsSchema(),
    selectedOpenTo: ProfileField.createSelectedOpenToSchema(),
    selectedRelationshipTypes: ProfileField.createSelectedRelationshipTypesSchema(),
    relationshipStatus: ProfileField.createRelationshipStatusSchema(t),
    selectedKinks: ProfileField.createSelectedKinksSchema(),
    photos: ProfileField.createPhotosSchema(t),
  });

export type ProfileFormValues = z.infer<ReturnType<typeof createProfileSchema>>;

export const ProfileSchemaKey = {
  NAME: 'name',
  BIRTHDAY: 'birthday',
  BIO: 'bio',
  HEIGHT: 'height',
  PRONOUNS: 'pronouns',
  CITY: 'city',
  GENDERS: 'selectedGenders',
  ORIENTATIONS: 'selectedOrientations',
  LANGUAGES: 'selectedLanguages',
  ETHNICITIES: 'selectedEthnicities',
  INTERESTS: 'selectedInterests',
  OPEN_TO: 'selectedOpenTo',
  RELATIONSHIP_TYPES: 'selectedRelationshipTypes',
  RELATIONSHIP_STATUS: 'relationshipStatus',
  KINKS: 'selectedKinks',
  PHOTOS: 'photos',
} as const;

export type ProfileSchemaKey = (typeof ProfileSchemaKey)[keyof typeof ProfileSchemaKey];

type FullShape = ReturnType<typeof createProfileSchema>['shape'];

export function useProfileSchema<const K extends readonly ProfileSchemaKey[]>(
  only: K
): z.ZodObject<Pick<FullShape, K[number]>>;
export function useProfileSchema(): z.ZodObject<FullShape>;
export function useProfileSchema<const K extends readonly ProfileSchemaKey[]>(only?: K) {
  const { t } = useTranslation('profile');
  const onlyKey = only ? only.slice().sort().join(',') : '';
  return useMemo(() => {
    const full = createProfileSchema(t);
    if (!onlyKey) {
      return full as z.ZodObject<FullShape>;
    }
    const selected = onlyKey.split(',') as K[number][];
    const shape = Object.fromEntries(selected.map(key => [key, full.shape[key]])) as Pick<
      FullShape,
      K[number]
    >;
    return z.object(shape) as z.ZodObject<Pick<FullShape, K[number]>>;
  }, [onlyKey, t]);
}

export function useOnboardingStepSchemas() {
  const full = useProfileSchema();
  return useMemo(
    () => [
      null,
      z.object({ userName: full.shape.name }),
      z.object({ photos: full.shape.photos }),
      z.object({
        selectedGenders: full.shape.selectedGenders,
        selectedOrientations: full.shape.selectedOrientations,
        pronouns: full.shape.pronouns,
      }),
      z.object({ city: full.shape.city }),
      z.object({ selectedLanguages: full.shape.selectedLanguages }),
      null,
      z.object({ height: full.shape.height }),
      z.object({
        relationshipStatus: full.shape.relationshipStatus,
        selectedRelationshipTypes: full.shape.selectedRelationshipTypes,
        selectedOpenTo: full.shape.selectedOpenTo,
      }),
      z.object({ selectedInterests: full.shape.selectedInterests }),
      null,
      null,
      z.object({ bio: full.shape.bio }),
      null,
      null,
      null,
    ],
    [full]
  );
}

type OnboardingStepSchema = ReturnType<typeof useOnboardingStepSchemas>[number];

export function validateOnboardingStep(
  step: number,
  data: unknown,
  schemas: readonly OnboardingStepSchema[]
): { isValid: boolean; error: string | null } {
  const schema = schemas[step];
  if (!schema) {
    return { isValid: true, error: null };
  }

  const result = schema.safeParse(data);
  if (!result.success) {
    const error = result.error.issues[0]?.message || 'Validation failed';
    return { isValid: false, error };
  }

  return { isValid: true, error: null };
}
